<script setup lang="ts">
import { copyToClipboard } from '@/utils/apostatudo/clipboard';
import { USER_V2_PATH_SCREENS } from '@/components/user-v2/types';

// Lista compartilhada dos históricos de apostas/transações do Minha Conta V2
// (ENG-362): filter tabs + cards expansíveis + paginação.
// Dados reais: mesma API das páginas antigas (useWalletStore.getTransactions →
// POST /bff/transactions, ver components/user/wallet/Transactions.vue).
interface FilterTab {
  key: string;
  label: string;
}

interface Props {
  filters: FilterTab[];
  // tab de filtro pré-selecionada (ex.: 'casino'/'sports' vindos das rotas
  // antigas de histórico); default = primeira tab
  initialFilter?: string;
}

const props = defineProps<Props>();

const { getTransactions } = useWalletStore();
const { transactions } = storeToRefs(useWalletStore());
const { showMoneyFormat } = useWallet();
const { openToaster } = useLayoutStore();

// ignora ?filter=/initialTab que não seja uma das tabs de verdade (ex.:
// ?filter=aaa) — sem isso, activeFilter aceitava qualquer string vinda da
// query e nem aparecia marcado em nenhuma tab da UI.
function isValidFilter(filter?: string): filter is string {
  return !!filter && props.filters.some((f) => f.key === filter);
}

const initialFilterWasInvalid =
  !!props.initialFilter && !isValidFilter(props.initialFilter);

const activeFilter = ref<string>(
  (isValidFilter(props.initialFilter) ? props.initialFilter : undefined) ||
    props.filters[0]?.key ||
    'full',
);

const { state, currentPath, syncAddressBar } = useUserV2Modal();

// histórico de apostas já tem rota própria por tab (/user/history/casino,
// /user/history/sports) — usa ela quando existir pra aquele screen+filtro.
function dedicatedPathFor(filter: string) {
  return Object.entries(USER_V2_PATH_SCREENS).find(
    ([, target]) =>
      target.screen === state.value.screen &&
      target.props?.initialTab === filter,
  )?.[0];
}

// mantém o link da tab ativa copiável (ex.: Cassino/Esporte no histórico de
// apostas, Depósito/Saque no de transações). Só a barra de endereço muda —
// nunca chama open()/state.props, que forçaria o Modal.vue a remontar a tela
// (a key do <component> inclui JSON.stringify(state.props)).
watch(activeFilter, (filter) => {
  // sem rota dedicada (ex.: histórico de transações): reflete a tab via
  // ?filter= no path canônico da tela (currentPath — nunca route.path: a
  // rota de verdade por trás do modal pode ser outra página qualquer, ou
  // até '/', ver comentário no middleware).
  syncAddressBar(
    dedicatedPathFor(filter) ?? `${currentPath.value}?filter=${filter}`,
  );
});

// corrige o link quando: (a) a tela abriu por uma rota "genérica" sem tab
// própria (ex.: menu "Histórico de apostas" → /user/history/full) mas a tab
// que já aparece ativa é outra (a primeira do FILTERS); ou (b) veio um
// ?filter=/initialTab inválido — nos dois casos a URL não bate com o que a
// tela realmente mostra. Sem rota dedicada e sem filtro inválido, o link
// inicial fica como já veio (ex.: "Todos" de transações não precisa de
// query nenhuma).
const initialDedicatedPath = dedicatedPathFor(activeFilter.value);

if (initialDedicatedPath) {
  syncAddressBar(initialDedicatedPath);
} else if (initialFilterWasInvalid) {
  syncAddressBar(currentPath.value);
}

const isLoading = ref<boolean>(false);

const items = computed<ITransaction[]>(() => transactions.value?.data || []);
const currentPage = computed(() => transactions.value?.current_page || 1);
const lastPage = computed(() => transactions.value?.last_page || 1);

// o design V2 não tem seletor de período — usa 'all' (extrato completo)
async function load(page = 1) {
  isLoading.value = true;

  try {
    await getTransactions(page, activeFilter.value, 'all');
  } catch {
    openToaster({ message: 'Algo deu errado!', type: 'error' });
  } finally {
    isLoading.value = false;
  }
}

watch(activeFilter, () => load(1));

onMounted(() => {
  load(1);
});

const STATUS_MAP: Record<
  string,
  { label: string; variant: 'success' | 'warning' | 'danger' }
> = {
  approved: { label: 'Aprovado', variant: 'success' },
  pending: { label: 'Pendente', variant: 'warning' },
  processing: { label: 'Processando', variant: 'warning' },
  denied: { label: 'Negado', variant: 'danger' },
  expired: { label: 'Expirado', variant: 'danger' },
  rolled_back: { label: 'Cancelado', variant: 'danger' },
  error: { label: 'Erro', variant: 'danger' },
  credit: { label: 'Entrada', variant: 'success' },
  debit: { label: 'Saída', variant: 'danger' },
  transfer: { label: 'Resgatado', variant: 'success' },
};

function statusOf(item: ITransaction) {
  return (
    STATUS_MAP[String(item.status || '').toLowerCase()] || {
      label: '-',
      variant: 'warning' as const,
    }
  );
}

// ícone do cabeçalho compacto: seta pra cima (crédito), pra baixo (débito),
// alerta (pendente/processando) — mesmo mapeamento da tabela antiga
function typeIconOf(item: ITransaction) {
  const type = String(item.type || '').toLowerCase();

  if (type === 'credit' || type === 'approved') {
    return { icon: 'up', classes: 'bg-v2-success/20 text-v2-success' };
  }

  if (type === 'debit') {
    return { icon: 'down', classes: 'bg-v2-danger/20 text-v2-danger' };
  }

  if (type === 'pending' || type === 'processing') {
    return { icon: 'alert', classes: 'bg-v2-warning/20 text-v2-warning' };
  }

  return { icon: 'alert', classes: 'bg-v2-danger/20 text-v2-danger' };
}

const ICON_PATHS: Record<string, string> = {
  up: 'M12 19V5m0 0-5 5m5-5 5 5',
  down: 'M12 5v14m0 0-5-5m5 5 5-5',
  alert: 'M12 8v4m0 4h.01M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z',
};

function amountOf(item: ITransaction) {
  if (item.is_freebet || item.is_freespin) {
    return String(item.amount);
  }

  return showMoneyFormat({ value: item.amount / 100 });
}

function sourceOf(item: ITransaction) {
  return item.player_desc || item.description || '-';
}

function bonusLabel(item: ITransaction) {
  if (item.is_freespin) {
    return 'Rodadas grátis';
  }

  if (item.is_freebet) {
    return 'Aposta grátis';
  }

  return '-';
}

function formatDate(date?: string) {
  if (!date) {
    return '-';
  }

  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return date;
  }

  return parsed.toLocaleString('pt-BR');
}

function rowsOf(item: ITransaction) {
  return [
    { label: 'Data', value: formatDate(item.created_at || item.updated_at) },
    { label: 'C/bônus', value: item.is_bonus ? 'Sim' : 'Não' },
    { label: 'Rodada', value: item.round_id ? String(item.round_id) : '-' },
    { label: 'Bônus', value: bonusLabel(item) },
    {
      label: 'Referência',
      value: item.reference ? String(item.reference) : '-',
    },
  ];
}

async function copyId(item: ITransaction) {
  const ok = await copyToClipboard(String(item.id));

  openToaster(
    ok
      ? { message: 'ID copiado!', type: 'success' }
      : { message: 'Não foi possível copiar o ID', type: 'error' },
  );
}

function changePage(page: number) {
  load(page);
}
</script>

<template>
  <!-- desktop: o box ocupa a altura toda e SÓ a lista rola (tabs e paginação fixas) -->
  <div
    class="border-solid flex flex-col gap-[16px] md:h-full md:min-h-0 md:rounded-[24px] md:border md:border-v2-default/40 md:p-[16px]"
  >
    <UserV2FilterTabs
      v-model="activeFilter"
      :tabs="props.filters"
    />

    <div
      v-if="isLoading"
      class="flex flex-col gap-[10px] md:min-h-0 md:flex-1 md:overflow-hidden"
    >
      <div
        v-for="n in 6"
        :key="`transactions-skeleton-${n}`"
        class="transactions-skeleton h-[64px] w-full rounded-[10px] bg-v2-default/40"
      />
    </div>

    <template v-else>
      <div
        v-if="items.length"
        class="user-v2-scroll flex flex-col gap-[10px] md:min-h-0 md:flex-1 md:overflow-y-auto md:pr-[6px]"
      >
        <UserV2HistoryCard
          v-for="(item, index) in items"
          :key="`${item.id}-${index}`"
          :rows="rowsOf(item)"
          :status="statusOf(item).label"
          :status-variant="statusOf(item).variant"
        >
          <span
            class="flex size-[32px] shrink-0 items-center justify-center rounded-full"
            :class="typeIconOf(item).classes"
          >
            <svg
              class="size-[16px]"
              fill="none"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
              viewBox="0 0 24 24"
            >
              <path :d="ICON_PATHS[typeIconOf(item).icon]" />
            </svg>
          </span>

          <span class="flex min-w-0 flex-1 flex-col gap-[2px]">
            <span
              class="flex items-center gap-[4px] text-[12px] font-medium leading-[16px]"
            >
              <span class="text-v2-default-800">ID:</span>
              <span class="truncate text-white">{{ item.id }}</span>
              <button
                class="shrink-0 text-white/60"
                title="Copiar ID"
                type="button"
                @click.stop="copyId(item)"
              >
                <svg
                  class="size-[12px]"
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.5"
                  viewBox="0 0 24 24"
                >
                  <path d="M8 8h10v12H8V8Zm0 0V4h10v2" />
                </svg>
              </button>
            </span>
            <span class="truncate text-[12px] leading-[16px]">
              <span class="text-v2-default-800">Fonte: </span>
              <span class="text-white"> {{ sourceOf(item) }}</span>
            </span>
          </span>

          <template #aside>
            <span class="text-[14px] font-semibold leading-[20px] text-white">
              {{ amountOf(item) }}
            </span>
          </template>
        </UserV2HistoryCard>
      </div>

      <p
        v-else
        class="rounded-[10px] bg-v2-default/40 p-[16px] text-center text-[12px] leading-[16px] text-v2-default-800"
      >
        Não há informações para exibir.
      </p>

      <UserV2Pagination
        v-if="lastPage > 1"
        :last-page="lastPage"
        :page="currentPage"
        @change="changePage"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
// main.scss reseta `transition` — skeleton pulsa via keyframes
.transactions-skeleton {
  animation: transactions-pulse 1.2s ease-in-out infinite;
}

@keyframes transactions-pulse {
  0%,
  100% {
    opacity: 0.45;
  }

  50% {
    opacity: 1;
  }
}
</style>
